import React, {useState, useEffect, useRef, useCallback} from 'react';
import Header from './Header';
import Footer from './Footer';
import MetronomeSection from './MetronomeSection';
import CharacterSection from './CharacterSection';
import AchievementsSection from './AchievementsSection';
import CharacterUnlockPopup from './CharacterUnlockPopup';
import AchievementPopup from './AchievementPopup';
import {useAudio} from '../hooks/useAudio';
import {useLanguage} from '../contexts/LanguageContext';
import {useTheme} from '../contexts/ThemeContext';
import {useCharacters} from '../hooks/useCharacters';
import {useAchievements} from '../hooks/useAchievements';
import {Helmet} from 'react-helmet-async';

const BeatBuddyApp = () => {
  const [activeTab, setActiveTab] = useState('metronome');
  const [isPlaying, setIsPlaying] = useState(false);
  const [tempo, setTempo] = useState(100);
  const [beatsPerMeasure, setBeatsPerMeasure] = useState(4);
  const [count, setCount] = useState(0);
  const [beatAccents, setBeatAccents] = useState([true, false, false, false]);
  const [practiceTime, setPracticeTime] = useState(0);
  const [totalPracticeTime, setTotalPracticeTime] = useState(() => {
    try {
      const saved = localStorage.getItem('beat-buddy-total-practice-time');
      return saved ? parseInt(saved, 10) : 0;
    } catch (error) {
      return 0;
    }
  });
  const [characterUnlockPopup, setCharacterUnlockPopup] = useState(null);
  const [lastCheckedTime, setLastCheckedTime] = useState(0);

  const {t, forceUpdate, language} = useLanguage();
  const {isDark} = useTheme();
  const {playAccentSound, playRegularSound, initAudio, isInitialized} = useAudio();
  const {
    currentCharacter,
    currentColor,
    currentAccessory,
    characterColors,
    unlockedCharacters,
    setCurrentCharacter,
    setCurrentColor,
    setCurrentAccessory,
    unlockCharacter
  } = useCharacters();
  const {
    achievements,
    checkTimeAchievements,
    checkTempoAchievements,
    checkTimeSigAchievements,
    checkAccessoryAchievements,
    checkCharacterAchievements,
    checkPrincessUnlock,
    checkSuperheroUnlock,
    achievementPopup,
    hideAchievementPopup
  } = useAchievements();

  const metronomeTimer = useRef(null);
  const practiceTimer = useRef(null);
  const isInitializingRef = useRef(false);

  // 動態 SEO 頁面標題和描述
  const getPageTitle = () => {
    const baseTitle = 'Beat Buddy - 互動式音樂節拍器';
    switch (activeTab) {
      case 'metronome':
        return `${t('tabMetronome')} | ${baseTitle}`;
      case 'character':
        return `${t('tabCharacter')} | ${baseTitle}`;
      case 'achievements':
        return `${t('tabAchievements')} | ${baseTitle}`;
      default:
        return baseTitle;
    }
  };

  const getPageDescription = () => {
    switch (activeTab) {
      case 'metronome':
        return `Beat Buddy 節拍器 - 速度${tempo}BPM，${beatsPerMeasure}拍。互動式設計幫助您提高節奏感和音樂練習效果。支援 40-240 BPM，自定義重音拍。`;
      case 'character':
        return `Beat Buddy 角色定制 - 選擇和自定義您的練習伙伴。目前已解鎖${unlockedCharacters.length}個角色，繼續練習解鎖更多！10種可愛角色等你收集。`;
      case 'achievements':
        return `Beat Buddy 成就系統 - 追蹤您的練習進度和解鎖的獎勵。總練習時間：${Math.floor(totalPracticeTime / 60)}分鐘。完成成就解鎖更多角色和功能。`;
      default:
        return 'Beat Buddy 是一款免費的互動式音樂節拍器，幫助音樂愛好者提高節奏感。可愛角色、成就系統和自定義功能讓練習更有趣！';
    }
  };

  const getStructuredData = () => {
    return {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: getPageTitle(),
      description: getPageDescription(),
      url: `https://beatbuddy.homy.hk/`,
      inLanguage: language === 'zh' ? 'zh-HK' : 'en',
      isPartOf: {
        '@type': 'WebApplication',
        name: 'Beat Buddy',
        applicationCategory: 'MusicApplication',
        operatingSystem: 'Web'
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首頁',
            item: 'https://beatbuddy.homy.hk/'
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: activeTab === 'metronome' ? '節拍器' : activeTab === 'character' ? '角色' : '成就',
            item: `https://beatbuddy.homy.hk/#${activeTab}`
          }
        ]
      }
    };
  };

  // Initialize last checked time
  useEffect(() => {
    try {
      const saved = localStorage.getItem('beat-buddy-last-checked-time');
      if (saved) {
        setLastCheckedTime(parseInt(saved, 10));
      }
    } catch (error) {
      console.error('Error loading last checked time:', error);
    }
  }, []);

  // Initialize beat accents
  useEffect(() => {
    const newBeatAccents = Array(beatsPerMeasure).fill(false);
    newBeatAccents[0] = true;
    setBeatAccents(newBeatAccents);
    setCount(0);
  }, [beatsPerMeasure]);

  // Check time achievements and character unlocks
  useEffect(() => {
    if (totalPracticeTime > lastCheckedTime) {
      console.log(`⏰ Time increased: ${lastCheckedTime} → ${totalPracticeTime} seconds`);
      const timeUnlocks = checkTimeAchievements(totalPracticeTime, lastCheckedTime);
      if (timeUnlocks.length > 0) {
        console.log(`🎉 Time unlocks: ${timeUnlocks.join(',')}`);
        timeUnlocks.forEach((characterId, index) => {
          setTimeout(() => {
            unlockCharacter(characterId);
            setTimeout(() => {
              setCharacterUnlockPopup(characterId);
            }, 500);
          }, index * 1000);
        });
      }
      setLastCheckedTime(totalPracticeTime);
      localStorage.setItem('beat-buddy-last-checked-time', totalPracticeTime.toString());
    }
  }, [totalPracticeTime, lastCheckedTime, checkTimeAchievements, unlockCharacter]);

  // Check special character unlocks
  useEffect(() => {
    if (checkPrincessUnlock(unlockedCharacters)) {
      console.log('🏰 Unlocking Princess!');
      unlockCharacter('princess');
      setTimeout(() => {
        setCharacterUnlockPopup('princess');
      }, 1500);
    }
    if (checkSuperheroUnlock(unlockedCharacters)) {
      console.log('🦸 Unlocking Superhero!');
      unlockCharacter('superhero');
      setTimeout(() => {
        setCharacterUnlockPopup('superhero');
      }, 2000);
    }
  }, [unlockedCharacters, achievements, checkPrincessUnlock, checkSuperheroUnlock, unlockCharacter]);

  // Initialize audio
  useEffect(() => {
    const handleFirstInteraction = async () => {
      if (!isInitialized() && !isInitializingRef.current) {
        isInitializingRef.current = true;
        try {
          await initAudio();
          console.log('🔊 Audio initialized');
        } catch (error) {
          console.error('Audio initialization failed:', error);
        } finally {
          isInitializingRef.current = false;
        }
      }
    };

    const events = ['click', 'touchstart', 'keydown'];
    events.forEach(event => {
      document.addEventListener(event, handleFirstInteraction, {once: true});
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleFirstInteraction);
      });
    };
  }, [initAudio, isInitialized]);

  // Save practice time
  useEffect(() => {
    localStorage.setItem('beat-buddy-total-practice-time', totalPracticeTime.toString());
  }, [totalPracticeTime]);

  const startMetronome = useCallback(async () => {
    console.log('▶️ Starting metronome...');
    if (!isInitialized()) {
      try {
        await initAudio();
        await new Promise(resolve => setTimeout(resolve, 200));
      } catch (error) {
        console.error('Failed to initialize audio:', error);
        return;
      }
    }

    setIsPlaying(true);
    practiceTimer.current = setInterval(() => {
      setPracticeTime(prev => prev + 1);
      setTotalPracticeTime(prev => prev + 1);
    }, 1000);

    // Play first beat immediately
    setCount(0);
    if (beatAccents[0]) {
      playAccentSound();
    } else {
      playRegularSound();
    }

    const interval = 60000 / tempo;
    let beatCounter = 1;
    metronomeTimer.current = setInterval(() => {
      const currentBeat = beatCounter % beatsPerMeasure;
      const isAccentBeat = beatAccents[currentBeat];
      if (isAccentBeat) {
        playAccentSound();
      } else {
        playRegularSound();
      }
      setCount(currentBeat);
      beatCounter++;
    }, interval);
  }, [tempo, beatsPerMeasure, beatAccents, initAudio, isInitialized, playAccentSound, playRegularSound]);

  const stopMetronome = useCallback(() => {
    console.log('⏹️ Stopping metronome...');
    setIsPlaying(false);
    if (metronomeTimer.current) {
      clearInterval(metronomeTimer.current);
      metronomeTimer.current = null;
    }
    if (practiceTimer.current) {
      clearInterval(practiceTimer.current);
      practiceTimer.current = null;
    }
    setPracticeTime(0);
    setCount(0);
  }, []);

  const toggleMetronome = useCallback(() => {
    if (isPlaying) {
      stopMetronome();
    } else {
      startMetronome();
    }
  }, [isPlaying, startMetronome, stopMetronome]);

  // Update metronome interval
  useEffect(() => {
    if (isPlaying && metronomeTimer.current) {
      clearInterval(metronomeTimer.current);
      const interval = 60000 / tempo;
      let beatCounter = (count + 1) % beatsPerMeasure;
      metronomeTimer.current = setInterval(() => {
        const currentBeat = beatCounter % beatsPerMeasure;
        const isAccentBeat = beatAccents[currentBeat];
        if (isAccentBeat) {
          playAccentSound();
        } else {
          playRegularSound();
        }
        setCount(currentBeat);
        beatCounter++;
      }, interval);
    }
  }, [tempo, isPlaying, beatAccents, beatsPerMeasure, count, playAccentSound, playRegularSound]);

  const handleTempoChange = (newTempo) => {
    console.log(`🎵 Tempo changed: ${tempo} → ${newTempo}`);
    setTempo(newTempo);
    const result = checkTempoAchievements(newTempo);
    if (result.shouldUnlockFox) {
      console.log('🦊 Unlocking Fox!');
      setTimeout(() => {
        unlockCharacter('fox');
        setTimeout(() => {
          setCharacterUnlockPopup('fox');
        }, 500);
      }, 1000);
    }
  };

  const handleTimeSigChange = (newBeats) => {
    console.log(`📊 Time signature changed: ${beatsPerMeasure} → ${newBeats}`);
    const wasPlaying = isPlaying;
    if (isPlaying) {
      stopMetronome();
    }
    setBeatsPerMeasure(newBeats);
    if (wasPlaying) {
      setTimeout(() => {
        startMetronome();
      }, 100);
    }
    const result = checkTimeSigAchievements(newBeats, unlockedCharacters);
    if (result.shouldUnlockNinja) {
      console.log('🥷 Unlocking Ninja!');
      setTimeout(() => {
        unlockCharacter('ninja');
        setTimeout(() => {
          setCharacterUnlockPopup('ninja');
        }, 500);
      }, 1000);
    }
  };

  const handleCharacterChange = (characterId) => {
    console.log(`🦄 Character changed: ${currentCharacter} → ${characterId}`);
    setCurrentCharacter(characterId);
    setCurrentColor(characterColors[characterId] || '#5D5CDE');
    if (characterId !== currentCharacter) {
      console.log('🎭 Character actually changed, checking achievement');
      checkCharacterAchievements();
    }
  };

  const handleAccessoryChange = (accessory) => {
    console.log(`👗 Accessory changed: ${currentAccessory} → ${accessory}`);
    setCurrentAccessory(accessory);
    const result = checkAccessoryAchievements(accessory);
    if (result.shouldUnlockAlien) {
      console.log('👽 Unlocking Alien!');
      setTimeout(() => {
        unlockCharacter('alien');
        setTimeout(() => {
          setCharacterUnlockPopup('alien');
        }, 500);
      }, 1000);
    }
  };

  const toggleBeatAccent = (beatIndex) => {
    setBeatAccents(prev => {
      const newAccents = [...prev];
      newAccents[beatIndex] = !newAccents[beatIndex];
      return newAccents;
    });
  };

  const handleCharacterSelectFromAchievements = (characterId) => {
    setCurrentCharacter(characterId);
    setCurrentColor(characterColors[characterId] || '#5D5CDE');
    setActiveTab('character');
  };

  return (
    <div
      key={`app-${forceUpdate}`}
      className="min-h-screen relative"
      style={{
        background: isDark
          ? 'radial-gradient(circle at 20% 90%, rgba(93, 92, 222, 0.2) 0%, transparent 30%), radial-gradient(circle at 90% 10%, rgba(97, 218, 251, 0.2) 0%, transparent 30%), radial-gradient(circle at 60% 40%, rgba(255, 222, 89, 0.15) 0%, transparent 40%), #0f0f23'
          : 'radial-gradient(circle at 20% 90%, rgba(93, 92, 222, 0.15) 0%, transparent 30%), radial-gradient(circle at 90% 10%, rgba(94, 23, 235, 0.15) 0%, transparent 30%), radial-gradient(circle at 60% 40%, rgba(255, 222, 89, 0.1) 0%, transparent 40%), #FFFFFF'
      }}
    >
      <Helmet>
        <title>{getPageTitle()}</title>
        <meta name="description" content={getPageDescription()} />
        <meta property="og:title" content={getPageTitle()} />
        <meta property="og:description" content={getPageDescription()} />
        <meta property="og:url" content="https://beatbuddy.homy.hk/" />
        <meta name="twitter:title" content={getPageTitle()} />
        <meta name="twitter:description" content={getPageDescription()} />
        <link rel="canonical" href="https://beatbuddy.homy.hk/" />
        <script type="application/ld+json">
          {JSON.stringify(getStructuredData())}
        </script>
      </Helmet>

      <div className="app-background">
        {isDark ? (
          <>
            <div className="dark-stars"></div>
            <div className="meteors"></div>
          </>
        ) : (
          <div className="light-stars"></div>
        )}
      </div>

      <div className="container mx-auto px-4 py-6 max-w-6xl relative z-10">
        <Header activeTab={activeTab} setActiveTab={setActiveTab} />

        <main className="mt-8">
          {activeTab === 'metronome' && (
            <MetronomeSection
              isPlaying={isPlaying}
              tempo={tempo}
              beatsPerMeasure={beatsPerMeasure}
              count={count}
              beatAccents={beatAccents}
              currentCharacter={currentCharacter}
              currentColor={currentColor}
              currentAccessory={currentAccessory}
              practiceTime={practiceTime}
              totalPracticeTime={totalPracticeTime}
              onToggleMetronome={toggleMetronome}
              onTempoChange={handleTempoChange}
              onTimeSigChange={handleTimeSigChange}
              onToggleBeatAccent={toggleBeatAccent}
            />
          )}

          {activeTab === 'character' && (
            <CharacterSection
              currentCharacter={currentCharacter}
              currentColor={currentColor}
              currentAccessory={currentAccessory}
              characterColors={characterColors}
              unlockedCharacters={unlockedCharacters}
              onCharacterChange={handleCharacterChange}
              onColorChange={setCurrentColor}
              onAccessoryChange={handleAccessoryChange}
            />
          )}

          {activeTab === 'achievements' && (
            <AchievementsSection
              achievements={achievements}
              recentAchievements={[]}
              totalPracticeTime={totalPracticeTime}
              currentCharacter={currentCharacter}
              unlockedCharacters={unlockedCharacters}
              onCharacterSelect={handleCharacterSelectFromAchievements}
            />
          )}
        </main>

        <Footer />
      </div>

      {characterUnlockPopup && (
        <div className="popup-animate">
          <CharacterUnlockPopup
            characterId={characterUnlockPopup}
            onClose={() => setCharacterUnlockPopup(null)}
          />
        </div>
      )}

      {achievementPopup && (
        <div className="popup-animate">
          <AchievementPopup
            title={achievementPopup.title}
            description={achievementPopup.description}
            emoji={achievementPopup.emoji}
            onClose={hideAchievementPopup}
          />
        </div>
      )}
    </div>
  );
};

export default BeatBuddyApp;